import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import {
  User,
  Instagram,
  Youtube,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  Plus,
  Trash2,
  Lock,
  Sparkles,
  ExternalLink,
  Palette,
  Globe,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Check
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Profile', icon: User },
  { id: 2, title: 'Links', icon: LinkIcon },
  { id: 3, title: 'Social', icon: Instagram },
  { id: 4, title: 'Theme', icon: Palette },
];

const THEMES = [
  { id: 'dark', name: 'Dark', bg: 'bg-gray-900', card: 'bg-gray-800 hover:bg-gray-700', text: 'text-white', subtext: 'text-gray-400' },
  { id: 'light', name: 'Light', bg: 'bg-gray-100', card: 'bg-white hover:bg-gray-50 border border-gray-200', text: 'text-gray-900', subtext: 'text-gray-600' },
  { id: 'gradient', name: 'Gradient', bg: 'bg-gradient-to-br from-purple-600 to-pink-500', card: 'bg-white/20 hover:bg-white/30 backdrop-blur', text: 'text-white', subtext: 'text-white/80' },
  { id: 'ocean', name: 'Ocean', bg: 'bg-gradient-to-br from-cyan-500 to-blue-600', card: 'bg-white/20 hover:bg-white/30 backdrop-blur', text: 'text-white', subtext: 'text-white/80' },
  { id: 'sunset', name: 'Sunset', bg: 'bg-gradient-to-br from-orange-500 to-pink-500', card: 'bg-white/20 hover:bg-white/30 backdrop-blur', text: 'text-white', subtext: 'text-white/80' },
  { id: 'forest', name: 'Forest', bg: 'bg-gradient-to-br from-green-600 to-emerald-500', card: 'bg-white/20 hover:bg-white/30 backdrop-blur', text: 'text-white', subtext: 'text-white/80' },
];

const LinkInBioGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [profile, setProfile] = useState({ name: '', bio: '', theme: 'dark' });
  const [links, setLinks] = useState([{ id: 1, title: '', url: '' }]);
  const [socials, setSocials] = useState({ instagram: '', youtube: '', twitter: '', linkedin: '' });
  
  const [showPreview, setShowPreview] = useState(false);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndX(null);
    setTouchStart(e.targetTouches[0].clientY);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !touchStartX || !touchEndX) return;
    
    const distanceY = touchStart - touchEnd;
    const distanceX = touchStartX - touchEndX;
    
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
    
    if (isHorizontalSwipe) {
      const isSwipeLeft = distanceX > minSwipeDistance;
      const isSwipeRight = distanceX < -minSwipeDistance;
      
      if (isSwipeLeft && !showPreview && currentStep < 4) {
        nextStep();
      } else if (isSwipeRight && !showPreview && currentStep > 1) {
        prevStep();
      }
    }
    // Removed swipe up/down for preview - only keep horizontal swipe for step navigation
  };

  const addLink = () => setLinks([...links, { id: Date.now(), title: '', url: '' }]);
  const removeLink = (id) => links.length > 1 && setLinks(links.filter(l => l.id !== id));
  const updateLink = (id, field, value) => setLinks(links.map(l => l.id === id ? { ...l, [field]: value } : l));

  const nextStep = () => {
    if (currentStep === 1 && !profile.name.trim()) {
      toast({ title: 'Name Required', description: 'Please enter display name', variant: 'destructive' });
      return;
    }
    if (currentStep < 4) setCurrentStep(prev => prev + 1);
    else setShowPreview(true);
  };

  const prevStep = () => currentStep > 1 && setCurrentStep(prev => prev - 1);

  const handleGetLink = () => {
    if (!user) {
      setShowSignInPrompt(true);
      return;
    }
    navigate('/card-builder');
  };

  const currentTheme = THEMES.find(t => t.id === profile.theme) || THEMES[0];

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-1.5 block">Display Name <span className="text-red-500">*</span></Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  placeholder="@yourname"
                  className="pl-10 h-12"
                  autoFocus
                />
              </div>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Bio</Label>
              <textarea
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                placeholder="Creator • Designer • Dreamer"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg resize-none"
                rows={2}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Your Links</Label>
              <Button variant="outline" size="sm" onClick={addLink}>
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            
            {links.map((link) => (
              <div key={link.id} className="flex gap-2">
                <div className="flex-1 space-y-2">
                  <Input
                    value={link.title}
                    onChange={(e) => updateLink(link.id, 'title', e.target.value)}
                    placeholder="Link Title"
                    className="h-10"
                  />
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={link.url}
                      onChange={(e) => updateLink(link.id, 'url', e.target.value)}
                      placeholder="https://..."
                      className="pl-10 h-10"
                    />
                  </div>
                </div>
                {links.length > 1 && (
                  <Button variant="ghost" size="icon" onClick={() => removeLink(link.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50 self-center">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium block mb-3">Social Icons</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={socials.instagram}
                  onChange={(e) => setSocials({ ...socials, instagram: e.target.value })}
                  placeholder="Instagram"
                  className="pl-10 h-11"
                />
              </div>
              <div className="relative">
                <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={socials.youtube}
                  onChange={(e) => setSocials({ ...socials, youtube: e.target.value })}
                  placeholder="YouTube"
                  className="pl-10 h-11"
                />
              </div>
              <div className="relative">
                <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={socials.twitter}
                  onChange={(e) => setSocials({ ...socials, twitter: e.target.value })}
                  placeholder="Twitter"
                  className="pl-10 h-11"
                />
              </div>
              <div className="relative">
                <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={socials.linkedin}
                  onChange={(e) => setSocials({ ...socials, linkedin: e.target.value })}
                  placeholder="LinkedIn"
                  className="pl-10 h-11"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium block mb-3">
              <Palette className="w-4 h-4 inline mr-1" /> Choose Theme
            </Label>
            <div className="grid grid-cols-3 gap-3">
              {THEMES.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => setProfile({ ...profile, theme: theme.id })}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    profile.theme === theme.id
                      ? 'border-pink-500 ring-2 ring-pink-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`h-10 rounded ${theme.bg}`}></div>
                  <span className="text-xs font-medium mt-2 block">{theme.name}</span>
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <SEO
        title="Free Link in Bio Generator | Create Bio Link Page"
        description="Create a free link in bio page for Instagram, TikTok, Twitter. Add multiple links, customize design. Linktree alternative."
        keywords="link in bio generator, bio link page, linktree alternative free, instagram bio link"
      />

      <div 
        className="min-h-screen bg-gray-50"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LinkIcon className="w-6 h-6 text-pink-500" />
              <span className="font-bold text-lg text-gray-900">Link in Bio</span>
            </div>
            <button onClick={() => setShowPreview(true)} className="text-pink-600 font-medium text-sm">
              Preview
            </button>
          </div>
        </header>

        {/* Progress Steps */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-lg mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center gap-1 ${currentStep >= step.id ? 'text-pink-600' : 'text-gray-400'}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      currentStep > step.id ? 'bg-pink-500 text-white' :
                      currentStep === step.id ? 'bg-pink-100 text-pink-600 border-2 border-pink-500' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {currentStep > step.id ? <Check className="w-3 h-3" /> : step.id}
                    </div>
                    <span className="text-xs font-medium hidden sm:inline">{step.title}</span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`w-6 sm:w-10 h-0.5 mx-1 ${currentStep > step.id ? 'bg-pink-500' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-lg mx-auto px-4 py-6 pb-32">
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {STEPS[currentStep - 1].title}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {currentStep === 1 && "Set up your profile"}
              {currentStep === 2 && "Add your important links"}
              {currentStep === 3 && "Connect your social accounts"}
              {currentStep === 4 && "Choose your page theme"}
            </p>
          </div>

          <Card className="shadow-sm border border-gray-100">
            <CardContent className="p-5">
              {renderStepContent()}
            </CardContent>
          </Card>
        </main>

        {/* Fixed Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="max-w-lg mx-auto px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep} className="flex-1 h-12">
                  <ChevronLeft className="w-5 h-5 mr-1" /> Back
                </Button>
              ) : (
                <div className="flex-1" />
              )}
              
              <Button
                onClick={nextStep}
                className="flex-1 h-12 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
              >
                {currentStep === 4 ? (
                  <>Preview Page</>
                ) : (
                  <>Next <ChevronRight className="w-5 h-5 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent 
          className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto p-0"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          <p className="text-xs text-center text-gray-400 mb-2">Swipe down to close</p>
          
          <DialogHeader className="px-4 pb-3 border-b">
            <DialogTitle className="text-lg font-semibold text-center">Your Bio Page</DialogTitle>
          </DialogHeader>
          
          <div className="p-4">
            <div className={`rounded-2xl ${currentTheme.bg} p-6 min-h-[350px]`}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-3">
                  <User className={`w-8 h-8 ${currentTheme.text}`} />
                </div>
                <h2 className={`text-lg font-bold ${currentTheme.text}`}>{profile.name || '@yourname'}</h2>
                <p className={`text-sm ${currentTheme.subtext}`}>{profile.bio || 'Your bio here'}</p>
              </div>

              <div className="space-y-2 mb-6">
                {links.filter(l => l.title || l.url).map(link => (
                  <div key={link.id} className={`${currentTheme.card} ${currentTheme.text} rounded-xl p-3 text-center text-sm transition-all cursor-pointer`}>
                    <span className="font-medium">{link.title || 'Link Title'}</span>
                    <ExternalLink className="w-3 h-3 inline ml-1 opacity-60" />
                  </div>
                ))}
                {links.filter(l => l.title || l.url).length === 0 && (
                  <div className={`${currentTheme.card} ${currentTheme.text} rounded-xl p-3 text-center text-sm`}>
                    <span className="font-medium">Your Link Here</span>
                  </div>
                )}
              </div>

              <div className="flex justify-center gap-4">
                {socials.instagram && <Instagram className={`w-5 h-5 ${currentTheme.text} opacity-80`} />}
                {socials.youtube && <Youtube className={`w-5 h-5 ${currentTheme.text} opacity-80`} />}
                {socials.twitter && <Twitter className={`w-5 h-5 ${currentTheme.text} opacity-80`} />}
                {socials.linkedin && <Linkedin className={`w-5 h-5 ${currentTheme.text} opacity-80`} />}
              </div>
            </div>

            <Button
              onClick={handleGetLink}
              className="w-full h-12 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white mt-4"
            >
              {user ? (
                <><LinkIcon className="w-5 h-5 mr-2" /> Create & Get Link</>
              ) : (
                <><Lock className="w-5 h-5 mr-2" /> Sign In to Get Link</>
              )}
            </Button>

            {showSignInPrompt && !user && (
              <div className="mt-4 p-4 bg-pink-50 rounded-xl border border-pink-200">
                <p className="text-pink-800 font-medium mb-3">Create a free account to get your bio link</p>
                <Button onClick={() => navigate('/card-builder')} className="bg-pink-600 hover:bg-pink-700">
                  Sign Up Free
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default LinkInBioGenerator;
